import SpoolSelector from "@bevco/Components/SpoolSelector";
import ProductQuantityOrdered, {
    ProductQuantityOrderedStylesProps,
} from "@bevco/Overrides/Components/ProductQuantityOrdered";
import useCustomQuoteRequired from "@bevco/Store/Handlers/Utilities/useCustomQuoteRequired";
import { CustomState } from "@bevco/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { HasProduct, ProductContextModel, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    return {
        pageType: getCurrentPage(state).type, // Verndale Customization: Putup price
    };
};

export interface SpoolsSelectorStyles {
    wrapper?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    quantityOrdered?: ProductQuantityOrderedStylesProps;
}

export const spoolsSelectorStyles: SpoolsSelectorStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 5px;

            &.wrapper-quote {
                gap: 10px;
                flex-direction: row;
                align-items: flex-end;
            }

            input[data-test-selector="product_qtyOrdered"] {
                min-inline-size: 100%;
            }
        `,
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            inline-size: 100%;
        `,
    },
    quantityOrdered: {
        wrapper: {
            css: css`
                min-inline-size: 100%;
            `,
        },
    },
};

type Props = WidgetProps & HasProduct & HasShellContext & ReturnType<typeof mapStateToProps>;

const styles = spoolsSelectorStyles;

const QuantityOrderedWrapper = styled.div<{ isPutUpPrice: boolean }>`
${({ isPutUpPrice }) => (isPutUpPrice ? "min-inline-size: 100%;" : "flex: 1 1 25%; min-inline-size: 25%;")}
`;

const AddToCartButtonWrapper = styled.div<{ isPutUpPrice: boolean }>`
${({ isPutUpPrice }) => (isPutUpPrice ? "inline-size: 100%;" : "flex: 1 1 50%;")}
`;

const ProductDetailsSpoolsSelector: React.FC<Props> = ({
    product,
    productInfo: { pricing },
    productInfo,
    shellContext,
    pageType,
}) => {
    const [numberOfSpools, setNumberOfSpools] = useState([1]);
    const [selectedNumberOfSpools, setSelectedNumberOfSpools] = useState("1");
    const [spoolsLength, setSpoolsLength] = useState(0);
    const [isPutUpPrice, setIsPutUpPrice] = useState(false);
    const { isInShell } = shellContext;
    const isPageDetail = pageType === "ProductDetailsPage";
    const [customerSpoolLength, setCustomerSpoolLength] = useState(0);
    const [isCmiProductForSpoolSelector, setIsCmiProductForSpoolSelector] = useState("");

    const productContext = {
        product,
        productInfo,
    } as ProductContextModel;

    if (!pricing?.additionalResults?.spools) {
        return null;
    }

    if (isInShell && isPageDetail) {
        return (
            <StyledWrapper
                css={css`
                    display: flex;
                    justify-content: center;
                `}
            >
                <Typography
                    css={css`
                        font-size: 16px;
                        color: #000;
                    `}
                >
                    Spool Selector Edit Mode
                </Typography>
            </StyledWrapper>
        );
    }

    const quoteRequired = useCustomQuoteRequired(productContext);

    useEffect(() => {
        setNumberOfSpools(getNumberOfSpools(pricing.additionalResults!.spools));
        setSpoolsLength(getSpoolsLength(productInfo.qtyOrdered, selectedNumberOfSpools));
    }, [productInfo, pricing.additionalResults.spools, selectedNumberOfSpools]);

    useEffect(() => {
        setSpoolsLength(getSpoolsLength(productInfo.qtyOrdered, selectedNumberOfSpools));
    }, [selectedNumberOfSpools]);

    useEffect(() => {
        const { allowSpoolOptions, customerLength, isCmiProduct } = productContext.product.properties;
        setIsPutUpPrice(allowSpoolOptions === "true" && !quoteRequired);
        setIsCmiProductForSpoolSelector(isCmiProduct);
        if(isCmiProduct && isCmiProduct.toLowerCase() === "true") {
            setCustomerSpoolLength(Number(customerLength.replace(/,/g, '')));
        }
    }, [productContext, quoteRequired]);

    const getNumberOfSpools = (value: string): number[] => {
        try {
            return JSON.parse(value);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Error parsing number of spools:", error);
            return [];
        }
    };

    const getSpoolsLength = (qty: number, selectedNumberOfSpools: string) => {
        const selectedSpools = parseInt(selectedNumberOfSpools, 10);
        if (Number.isNaN(selectedSpools) || selectedSpools === 0) {
            // eslint-disable-next-line no-console
            console.error("Invalid selected number of spools:", selectedNumberOfSpools);
            return 0;
        }
        return qty / selectedSpools;
    };
   

    return (
        <StyledWrapper {...styles.wrapper} className={`${!isPutUpPrice ? "wrapper-quote" : ""}`}>
            <QuantityOrderedWrapper isPutUpPrice={isPutUpPrice}>
                <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} labelPosition="top" />
            </QuantityOrderedWrapper>

            {isPutUpPrice && (
                <SpoolSelector
                    selectedNumberOfSpools={selectedNumberOfSpools}
                    setSelectedNumberOfSpools={setSelectedNumberOfSpools}
                    numberOfSpools={numberOfSpools}
                    spoolsLength={spoolsLength}
                    uom={productInfo.unitOfMeasure}
                    qtyOrdered={productInfo.qtyOrdered}
                    productId={productInfo.productId}
                    customerSpoolLength={customerSpoolLength}
                    isCmiProduct={isCmiProductForSpoolSelector}
                />
            )}

            <AddToCartButtonWrapper isPutUpPrice={isPutUpPrice}>
                <ProductAddToCartButton
                    data-test-selector="addToCartBtn"
                    extendedStyles={styles.productAddToCartButtonStyles}
                />
            </AddToCartButtonWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(withIsInShell(ProductDetailsSpoolsSelector))),
    definition: {
        displayName: "Spools Selector",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
